import { useEffect, useState } from "react";
import { JobPartCategory } from "../../models/Job";
import classNames from "../../utils/classNames";
import openInNewTab from "../../utils/openInNewTab";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import RsCoreImage from "../core/RsCoreImage";
import Modal from "../../layouts/Modal";
import { ArchiveBoxIcon, ClipboardDocumentListIcon, DocumentTextIcon, MapIcon, MapPinIcon, PencilSquareIcon, QuestionMarkCircleIcon, ShieldExclamationIcon } from "@heroicons/react/24/outline";
import PartConditionPickerButton from "../shared/PartConditionPicker/PartConditionPickerButton";
import useSinglePart from "../../data/useSinglePart";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import SelectInput from "../shared/Inputs/SelectInput";
import { useForm } from "react-hook-form";
import NotesOutput from "../shared/Notes/NotesOutput";
import { ResourceType } from "../../models/Note";
import { Assignee, IPart, PhysicalRouting } from "../../models/Part";
import JobPartAspects from "./JobPartAspects";
import VehicleLocationDropdown from "../vehicles/VehicleLocationDropdown";
import { IVehicle } from "../../models/Vehicle";
import WarehouseLocationDropdown from "../warehouseLocations/WarehouseLocationDropdown";
import useSinglePartMutations from "../../data/useSinglePartMutations";
import useFlowTypeAccounts from "../../data/useFlowTypeAccounts";
import { CheckFeatureFlag } from "../../hooks/useFeatureFlag";


interface JobPartProps {
  partCategory: JobPartCategory;
  markAsRemoved: (partData: JobPartCategory['partData']) => void;
  setDamaged: (partData: JobPartCategory['partData']) => void;
  setDamagedOpen: (open: boolean) => void;
  handlePrint: (partData: JobPartCategory['partData']) => void;
  selectedPartIds: Set<string>;
  togglePartSelection: (partId: string | null | undefined) => void;
  onDiagramClick?: (partCategory: JobPartCategory) => void;
}

export default function JobPart({ partCategory, markAsRemoved, setDamaged, setDamagedOpen, handlePrint, selectedPartIds, togglePartSelection, onDiagramClick }: JobPartProps) {
  const { saveHandlers } = useDefaultCRUDHandlers("Part");
  const { patchPart } = useSinglePartMutations(partCategory.partDataId ?? "")
  const { flowTypeAccounts } = useFlowTypeAccounts("JobPart");
  const hasTmeFeature = CheckFeatureFlag("TME");
  const { register, watch, setValue: setFormValue } = useForm<Record<string, any>>({
    defaultValues: { grade: "", accountId: "" },
  });

  const [open, setOpen] = useState(false);
  const [isSafetySelected, setIsSafetySelected] = useState(true);
  const [gradeInfoOpen, setGradeInfoOpen] = useState(false);
  const [storageOpen, setStorageOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [conditionOpen, setConditionOpen] = useState(false);
  const [locationOpen, setLocationOpen] = useState(false);

  const [partData, setPartData] = useState<IPart | null>(partCategory.partData ?? null);

  useEffect(() => {
    setPartData(partCategory.partData ?? null);
  }, [partCategory.partData]);

  useEffect(() => {
    setFormValue("grade", partData?.grade ?? "");
  }, [partData?.grade, setFormValue]);

  useEffect(() => {
    setFormValue("accountId", partData?.accountId ?? "");
  }, [partData?.accountId, setFormValue]);

  // Flow-type accounts the part can be assigned to. A blank option lets the
  // user clear the assignment.
  const accountOptions = [
    { id: "", name: "No Account" },
    ...(flowTypeAccounts.data?.data ?? []).map((account) => ({
      id: account.id,
      name: account.name,
    })),
  ];

  const partCategoryDefault = partData?.category?.partCategoryDefaults?.[0];
  const ebayCategoryId = partCategoryDefault?.ebayCategoryId ?? "";
  const dismantlerAspectNames = (partCategoryDefault?.aspectAssignees ?? [])
    .filter((aspectAssignee) => aspectAssignee.assignee === Assignee.Dismantler && aspectAssignee.aspectName)
    .map((aspectAssignee) => aspectAssignee.aspectName as string);

  const isSelected = partData?.id ? selectedPartIds.has(partData.id) : false;
  const isDeleted = !!(partData as any)?.deleted;
  const noteCount = partData?.internalNotes?.length ?? 0;
  const conditionNoteCount = partData?.notes?.length ?? 0;
  const categoryMaterials = partData?.category?.partCategoryMaterials ?? partCategory.partCategory?.partCategoryMaterials ?? [];
  const partCategoryMaterialsSubtitle = categoryMaterials.length > 0
    ? categoryMaterials
      .map((link) => {
        const percentage = Number(link.materialPercentage);
        const formattedPercentage = Number.isInteger(percentage) ? percentage.toString() : percentage.toFixed(2).replace(/\.?0+$/, "");
        return `${formattedPercentage}% ${link.material?.name ?? ""}`.trim();
      })
      .join(" - ")
    : "";

  const options = [
    { id: "A", name: "Grade A" },
    { id: "B", name: "Grade B" },
    { id: "C", name: "Grade C" },
    { id: "U", name: "Grade U" },
  ]

  const handleOpenNote = (isSafety: boolean) => {
    setIsSafetySelected(isSafety);
    setOpen(true);
  }

  const handleChangeMpn = (partNumber: string) => {

    if (partNumber == partData?.partNumber) return;

    if (!partNumber && !partData?.partNumber) return;

    const body: any = {
      partId: partCategory.partDataId,
      partNumber: partNumber
    };

    patchPart.mutate(body, saveHandlers);
  }

  const handleChangeGrade = (registerName: string, grade: string) => {
    setFormValue(registerName, grade);

    if (grade == partData?.grade) return;
    if (!grade && !partData?.grade) return;

    const body: any = {
      partId: partCategory.partDataId,
      grade
    };

    patchPart.mutate(body, saveHandlers);
  }

  const handleChangeAccount = (registerName: string, accountId: string) => {
    setFormValue(registerName, accountId);

    const newAccountId = accountId || null;
    if (newAccountId === (partData?.accountId ?? null)) return;

    // Mirror the backend defaulting: when assigning an account and the part has
    // no physical routing yet, adopt the account's default route.
    const selectedAccount = flowTypeAccounts.data?.data?.find((account) => account.id === newAccountId);
    const accountRouting = selectedAccount?.physicalRoutingDefault;
    const hasNoRouting = partData?.physicalRouting == null || partData.physicalRouting === PhysicalRouting.None;
    const adoptedRouting = hasNoRouting && accountRouting != null && accountRouting !== PhysicalRouting.None
      ? accountRouting
      : undefined;

    // Update the local part immediately so actions that read it (e.g. printing
    // the label) use the new account without waiting for a job refetch.
    setPartData((prev) => (prev ? { ...prev, accountId: newAccountId, ...(adoptedRouting !== undefined ? { physicalRouting: adoptedRouting } : {}) } : prev));

    const body: any = {
      partId: partCategory.partDataId,
      accountId: newAccountId,
      updateAccount: true,
    };

    patchPart.mutate(body, saveHandlers);
  }

  const handleChangeWeight = (weightInput: string) => {
    const trimmedWeight = (weightInput ?? "").trim();
    const parsedWeight = trimmedWeight === "" ? null : Number(trimmedWeight);

    if (trimmedWeight !== "" && Number.isNaN(parsedWeight)) return;
    if (parsedWeight == partData?.weight) return;
    if (parsedWeight === null && partData?.weight == null) return;

    const body: any = {
      partId: partCategory.partDataId,
      weight: parsedWeight
    };

    patchPart.mutate(body, saveHandlers);
  }

  return (
    <>
      <Modal open={open} setOpen={setOpen} width="max-w-4xl">
        <div className="space-y-2">
          <h1 className="text-lg font-semibold">{isSafetySelected ? "Safety Information" : "Dismantling Instructions"}</h1>

          <hr />

          <p className="text-gray-700">{isSafetySelected ? partCategory.partCategory?.safetyNote : partCategory.partCategory?.dismantlingInstructions}</p>
        </div>
      </Modal>

      <Modal open={gradeInfoOpen} setOpen={setGradeInfoOpen} width="max-w-2xl">
        <div className="space-y-3">
          <h1 className="text-lg font-semibold text-gray-800">Grading criteria</h1>
          <p className="text-sm text-gray-600">Requirements for each grade when assessing part condition.</p>

          <div className="space-y-2 text-sm text-gray-700">
            <div className="rounded-sm bg-gray-100 px-3 py-2">
              <p className="font-semibold text-gray-800">Grade A - Excellent</p>
              <p>Highest quality part. No, or minimal, damage, wear or corrosion. Fully functional, as new.</p>
            </div>

            <div className="rounded-sm bg-gray-100 px-3 py-2">
              <p className="font-semibold text-gray-800">Grade B - Good</p>
              <p>Good condition. Small or moderate amount of damage or wear. Fully functional.</p>
            </div>

            <div className="rounded-sm bg-gray-100 px-3 py-2">
              <p className="font-semibold text-gray-800">Grade C - Satisfactory</p>
              <p>Fair condition. Some wear or minor damage. Functional with possible cosmetic issues.</p>
            </div>

            <div className="rounded-sm bg-gray-100 px-3 py-2">
              <p className="font-semibold text-gray-800">Grade U - Not Tested/Out Of Scope</p>
              <p>Lowest quality part. Significant wear, damage or corrosion. May have functional issues.</p>
            </div>
          </div>
        </div>
      </Modal>

      <Modal open={storageOpen} setOpen={setStorageOpen} width="max-w-2xl">
        <div className="space-y-2">
          <h1 className="text-lg font-semibold">Storage Instructions</h1>
          <hr />
          <p className="text-gray-700">{partCategory.partCategory?.storageNote}</p>
        </div>
      </Modal>

      <Modal open={notesOpen} setOpen={setNotesOpen} width="max-w-3xl">
        <div className="space-y-2">
          <h1 className="text-lg font-semibold">Notes</h1>
          <hr />
          <NotesOutput
            notes={partData?.internalNotes ?? []}
            resourceType={ResourceType.InternalPart}
            resourceId={partCategory.partDataId ?? ""}
            refetchQuery={["parts", partData?.id]}
            shouldScroll={false}
          />
        </div>
      </Modal>

      <Modal open={conditionOpen} setOpen={setConditionOpen} width="max-w-3xl">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-lg font-semibold">Condition Notes</h1>
              <p className="text-sm text-gray-500">These notes will go onto the listing</p>
            </div>
            <PartConditionPickerButton
              resourceId={partCategory.partDataId ?? ""}
              resourceType={ResourceType.Part}
              refetchQuery={["parts", partData?.id]}
            />
          </div>
          <hr />
          <NotesOutput
            notes={partData?.notes ?? []}
            resourceType={ResourceType.Part}
            resourceId={partCategory.partDataId ?? ""}
            refetchQuery={["parts", partData?.id]}
            shouldScroll={false}
          />
        </div>
      </Modal>

      <Modal open={locationOpen} setOpen={setLocationOpen} width="max-w-3xl">
        <div className="space-y-2">
          <h1 className="text-lg font-semibold">Location</h1>
          <hr />
          <WarehouseLocationDropdown part={partData} />
        </div>
      </Modal>

      <div key={partCategory.partDataId} className={`p-4 rounded-md flex flex-col gap-y-3 ${isDeleted ? 'bg-red-100' : 'bg-white'} ${isSelected ? 'ring-2 ring-blue-500' : ''}`}>

        <div className="flex gap-x-2 items-center">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => togglePartSelection(partData?.id)}
            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
            disabled={!partData?.id}
          />
          <p className="text-lg font-semibold">{partData?.category?.name} - <span className="text-base font-medium">{partData?.tagNumber}</span></p>

          {partData?.removedOn && (
            <p className="font-semibold text-green-600">REMOVED</p>
          )}
          {partData?.isDamaged && (
            <p className="font-semibold text-red-600">DAMAGED</p>
          )}
          {partData?.isCore && (
            <RsCoreImage className="ml-auto !h-6" />
          )}
        </div>

        {partData?.physicalRouting != null && partData.physicalRouting !== PhysicalRouting.None && (
          <p className="text-sm text-gray-600">{PhysicalRouting[partData.physicalRouting]}</p>
        )}

        {partCategoryMaterialsSubtitle && (
          <p className="text-sm text-gray-600">
            {partCategoryMaterialsSubtitle}
          </p>
        )}

        <div className="flex justify-between items-start gap-x-4">
          <div className="flex flex-col gap-y-3">
            <div className="flex flex-wrap gap-2">
              {partCategory.partCategory?.safetyNote &&
                <button
                  onClick={() => handleOpenNote(true)}
                  className="flex gap-x-1 items-center text-xs text-gray-600 hover:bg-gray-100 rounded-lg p-1"
                >
                  <ShieldExclamationIcon className="h-4 w-4 text-amber-500" /> Safety
                </button>
              }

              {partCategory.partCategory?.dismantlingInstructions &&
                <button
                  onClick={() => handleOpenNote(false)}
                  className="flex gap-x-1 items-center text-xs text-gray-600 hover:bg-gray-100 rounded-lg p-1"
                >
                  <ClipboardDocumentListIcon className="h-4 w-4 text-blue-500" /> Instructions
                </button>
              }

              {partCategory.partCategory?.storageNote &&
                <button
                  onClick={() => setStorageOpen(true)}
                  className="flex gap-x-1 items-center text-xs text-gray-600 hover:bg-gray-100 rounded-lg p-1"
                >
                  <ArchiveBoxIcon className="h-4 w-4 text-purple-500" /> Storage
                </button>
              }

              <button
                onClick={() => setNotesOpen(true)}
                className="flex gap-x-1 items-center text-xs text-gray-600 hover:bg-gray-100 rounded-lg p-1"
              >
                <PencilSquareIcon className="h-4 w-4 text-green-500" /> Notes {noteCount > 0 && `(${noteCount})`}
              </button>

              <button
                onClick={() => setConditionOpen(true)}
                className="flex gap-x-1 items-center text-xs text-gray-600 hover:bg-gray-100 rounded-lg p-1"
              >
                <DocumentTextIcon className="h-4 w-4 text-primary-500" /> Condition {conditionNoteCount > 0 && `(${conditionNoteCount})`}
              </button>

              <button
                onClick={() => setLocationOpen(true)}
                className="flex gap-x-1 items-center text-xs text-gray-600 hover:bg-gray-100 rounded-lg p-1"
              >
                <MapPinIcon className="h-4 w-4 text-amber-500" /> Location
              </button>

            </div>

            {hasTmeFeature && (
              <div>
                <SelectInput
                  label={"Account"}
                  register={register}
                  registerName={"accountId"}
                  options={accountOptions}
                  setValue={handleChangeAccount}
                  watch={watch}
                  labelPosition="inline"
                  placeholder="No Account"
                  size="sm"
                  cols="w-64"
                  classes="rounded"
                />
              </div>
            )}
          </div>

          {partData?.id && (
            <img
              src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${partData.id}`}
              alt="Part QR code"
              className="h-24 w-24 shrink-0"
            />
          )}
        </div>

        <div>
          <p>OE MPN: {partData?.oePartNumber ?? "-"}</p>
        </div>

        <div className="flex flex-wrap justify-between items-center gap-2 text-gray-700">


          <div className="flex gap-2 items-center">
            <label className="text-sm font-medium text-gray-500">MPN</label>
            <input
              onBlur={(e) => handleChangeMpn(e.target.value)}
              type="string"
              defaultValue={partData?.partNumber ?? ""}
              className="px-2 py-1 text-sm rounded border border-gray-300"
              placeholder="Add MPN"
            />
          </div>

          <div className="flex items-center gap-x-2">
            <SelectInput
              label={"Grade"}
              register={register}
              registerName={"grade"}
              options={options}
              setValue={handleChangeGrade}
              watch={watch}
              labelPosition="inline"
              placeholder="Select Grade"
              size="sm"
              cols="w-48"
              classes="rounded"
            />
            <button
              type="button"
              onClick={() => setGradeInfoOpen(true)}
              className="text-gray-500 hover:text-gray-700"
              aria-label="Open grading criteria"
            >
              <QuestionMarkCircleIcon className="h-5 w-5" />
            </button>
          </div>

          <div className="flex gap-2 items-center">
            <label className="text-sm font-medium text-gray-500">Weight</label>
            <input
              onBlur={(e) => handleChangeWeight(e.target.value)}
              type="number"
              step=".01"
              defaultValue={partData?.weight ?? ""}
              className="px-2 py-1 text-sm rounded border border-gray-300 w-28"
              placeholder="kg"
            />
          </div>
        </div>

        {partData?.id && ebayCategoryId && dismantlerAspectNames.length > 0 && (
          <JobPartAspects
            partDataId={partData.id}
            ebayCategoryId={ebayCategoryId}
            dismantlerAspectNames={dismantlerAspectNames}
            existingAspects={partData.aspects ?? []}
            // onSaved={() => singlePart.refetch()}
          />
        )}

        <hr />

        <div className="flex items-center gap-x-3">
          {onDiagramClick && partCategory.partCategory && (() => {
            const canView = partCategory.partCategory!.ucidZone && partData?.matchedUcid;
            return (
              <ButtonInput
                label={canView ? "View Diagram" : "Find Diagram"}
                isSubmit={false}
                onClick={() => onDiagramClick(partCategory)}
                classes={classNames("!py-1 !px-1 ", canView
                  ? "!bg-primary-100 !text-primary-700 hover:!bg-primary-200"
                  : "!bg-gray-200 !text-gray-600 hover:!bg-gray-300")}
              />
            );
          })()}
        </div>

        <div className="flex flex-col flex-wrap md:flex-row gap-x-4 gap-y-2">

          {!partData?.isDamaged && (
            <>
              <ButtonInput
                label={"Mark Removed"}
                isSubmit={false}
                onClick={() => markAsRemoved(partData!)}
                classes={classNames(partData?.removedOn ? "" : "!bg-green-600 hover:!bg-green-700", "!py-1 !px-1")}
                disabled={partData?.removedOn !== null}
              />
              <ButtonInput
                label={"Mark Damaged"}
                isSubmit={false}
                onClick={() => { setDamaged(partData); setDamagedOpen(true); }}
                classes={"!py-1 !px-1 !bg-red-600 hover:!bg-red-700"}
              />
            </>
          )}

          <ButtonInput
            label={"View Stock"}
            isSubmit={false}
            onClick={() => openInNewTab(`/stock/${partData?.id}`)}
            classes={"!py-1 !px-1 "}
          />

          <ButtonInput
            label={"Print Label"}
            isSubmit={false}
            onClick={() => handlePrint(partData!)}
            classes={"!py-1 !px-1 "}
          />
        </div>
      </div>
    </>
  )
}